/**
 * Main Chart React component
 * Combines all hooks and renders the visualization
 */

import React, { Fragment, useState, useMemo, useEffect, useRef, useContext } from 'react'
import PropTypes from 'prop-types'

import {
  useExportableChart,
  svgToPngDataUrl,
  ExportContext
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import { useFillChartFrame } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/chart-frame'
import { CLOUD_INSET, DEFAULT_CONFIG } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/config'

import { useColorScale } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useColorScale'
import { useFontLoader } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useFontLoader'
import { useMaskRenderer } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useMaskRenderer'
import { useCloudLayout } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useCloudLayout'
import { useRenderGate } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'
import { embedFont } from '@aytm/app/stats/page/question-views/components/word-cloud/utils/embedFont'
import { removeCloudCorners } from '@aytm/app/stats/page/question-views/components/word-cloud/utils/removeCloudCorners'

import CloudSVG from '@aytm/app/stats/page/question-views/components/word-cloud/components/CloudSVG'

import style from '@aytm/app/stats/page/question-views/components/word-cloud/styles/Chart.module.scss'

// The crumbs are plain spans, so they are put in the tab order and answer Enter themselves: a
// keyboard user has to be able to climb back out of a word as a mouse user can.
const onEnter = action => event => {
  if ('Enter' === event.key) action()
}

/**
 * Chart Component
 *
 * Standalone React component for rendering word clouds.
 * Designed for integration with existing Backbone architecture via bridge pattern.
 */
const WordCloudChart = React.forwardRef(
  ({ width, height, model, words, config, scope, onParamsChange, onWordClick, isWordClickable }, ref) => {
    const { useRawData } = model

    // the layout is handed the inset box, not the full one, so the margin the cloud stands off by
    // stays clear instead of being a strip a word can land on
    const innerWidth = Math.max(0, width - 2 * CLOUD_INSET)
    const innerHeight = Math.max(0, height - 2 * CLOUD_INSET)

    // Extract config values with defaults
    const {
      activeWord = DEFAULT_CONFIG.activeWord,
      fontFamily = DEFAULT_CONFIG.fontFamily,
      colorStyle = DEFAULT_CONFIG.colorStyle,
      colors = DEFAULT_CONFIG.colors,
      background = DEFAULT_CONFIG.background,
      mask = DEFAULT_CONFIG.mask,
      customMaskPath = DEFAULT_CONFIG.customMaskPath,
      angles = DEFAULT_CONFIG.angles,
      scaleFunction = DEFAULT_CONFIG.scaleFunction,
      rangeStart = DEFAULT_CONFIG.rangeStart,
      rangeEnd = DEFAULT_CONFIG.rangeEnd,
      mergeWords = DEFAULT_CONFIG.mergeWords,
      randIndex = DEFAULT_CONFIG.randIndex,
      orientation = DEFAULT_CONFIG.orientation
    } = config

    const [isRendering, setIsRendering] = useState(true)

    const cloudDrawn = useRenderGate()

    // Headless export mode. Layout guards and the empty-cloud SVG below hang off
    // this, so the live page keeps its exact current behaviour (requestId is null).
    const { requestId } = useContext(ExportContext)

    // Load font
    const { isLoaded: fontLoaded } = useFontLoader(fontFamily)

    // Generate mask
    const {
      mask32,
      maskBound,
      isReady: maskReady
    } = useMaskRenderer(mask, orientation, innerWidth, innerHeight, customMaskPath)

    // Compute layout
    const layoutOptions = useMemo(() => {
      return {
        width: innerWidth,
        height: innerHeight,
        words,
        fontFamily,
        angles,
        scaleFunction,
        rangeStart,
        rangeEnd,
        mask32: maskReady ? mask32 : [],
        maskBound: maskReady ? maskBound : [],
        randIndex,
        mergeWords: mergeWords === 1,
        boundedRuntime: Boolean(requestId)
      }
    }, [
      innerWidth,
      innerHeight,
      words,
      fontFamily,
      angles,
      scaleFunction,
      rangeStart,
      rangeEnd,
      mask32,
      maskBound,
      maskReady,
      randIndex,
      mergeWords,
      requestId
    ])

    const { layoutWords, isComputing } = useCloudLayout(layoutOptions)

    // Calculate size distribution for color scale
    const sizes = useMemo(() => {
      const sizeMap = {}
      for (const word of layoutWords) {
        sizeMap[word.size] = (sizeMap[word.size] || 0) + 1
      }
      return sizeMap
    }, [layoutWords])

    // Calculate actual range from layout
    const [realRangeFrom, realRangeTo] = useMemo(() => {
      if (layoutWords.length === 0) return [rangeStart, rangeEnd]

      let min = Infinity
      let max = -Infinity

      for (const word of layoutWords) {
        min = Math.min(min, word.size)
        max = Math.max(max, word.size)
      }

      return [min, max]
    }, [layoutWords, rangeStart, rangeEnd])

    // Create color function
    const colorFn = useColorScale(colorStyle, colors, realRangeFrom, realRangeTo, sizes)

    const [currentState, setCurrentState] = useState({
      width: innerWidth,
      height: innerHeight,
      fontFamily,
      background,
      colorFn,
      layoutWords: []
    })

    // Notify when rendering is complete. The laid-out words are a dependency, not just a value the
    // publish reads: a layout can land without the three gates moving, and a publish that waits for
    // one of them to move leaves the loader up over words that are ready to draw.
    //
    // A layout that placed nothing has still finished: the placeholder answers for a cloud that has
    // not settled, and holding it over a settled empty corpus says "still loading" forever, which is
    // the one thing it must not say. An empty cloud is what the legacy build drew for this.
    // eslint-disable-next-line consistent-return
    useEffect(() => {
      const settled = fontLoaded && maskReady && !isComputing
      setIsRendering(!settled)

      settled &&
        0 < layoutWords.length &&
        setCurrentState({ width: innerWidth, height: innerHeight, fontFamily, background, colorFn, layoutWords })

      // An export waits on this one too, and stalls to its timeout if it is held for an empty layout.
      if (settled) cloudDrawn()
    }, [layoutWords, fontLoaded, maskReady, isComputing, cloudDrawn])

    useEffect(
      () => setIsRendering(true),
      [
        innerWidth,
        innerHeight,
        words,
        fontFamily,
        angles,
        scaleFunction,
        rangeStart,
        rangeEnd,
        mask32,
        maskBound,
        maskReady,
        randIndex,
        mergeWords
      ]
    )

    // Headless export: capture the CloudSVG <svg>. cloudRef falls back to an
    // internal ref so the live page can still pass its own ref through.
    const internalRef = useRef(null)
    const cloudRef = ref || internalRef

    // what the placeholder above stood in for is this cloud, so it is this cloud that answers for it
    useFillChartFrame(cloudRef)

    useExportableChart({
      debugLabel: 'WordCloudChart',
      deps: [fontLoaded, maskReady, !isRendering],
      prepareForExport: async () => {
        const svgEl = cloudRef.current
        if (!svgEl) throw new Error('WordCloud SVG ref not attached')
        // CloudSVG re-centers the word group (getBBox ~50ms after mount) and fades
        // words in via an opacity transition. Let centering settle, then force
        // opacity on a clone so the PNG never captures a mid-fade (blank) frame.
        await new Promise(resolve => setTimeout(resolve, 200))
        const clone = removeCloudCorners(svgEl.cloneNode(true))
        clone.querySelectorAll('text').forEach(node => {
          node.style.opacity = '1'
          node.style.transition = 'none'
        })
        // The rasterizer renders the clone as a standalone document with no access to the page's
        // fonts — the face has to travel inside the markup.
        await embedFont(clone, currentState.fontFamily)
        const svgString =
          '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(clone)
        return [await svgToPngDataUrl(svgString, innerWidth, innerHeight)]
      }
    })

    return (
      <div
        className={style.container}
        style={{
          width: currentState.width,
          height: currentState.height,
          padding: CLOUD_INSET,
          '--wc-inset': `${CLOUD_INSET}px`
        }}
      >
        {isRendering ? (
          <div className={style.loader}>
            <span className={style.spinner} />
          </div>
        ) : null}

        {/* Active word breadcrumb */}
        {0 < scope?.length ? (
          <div className={style.links}>
            <span
              data-testid="stat-wc-crumb-all"
              className={style.link}
              tabIndex={0}
              onClick={() => onWordClick({ scope: ['*'] })}
              onKeyDown={onEnter(() => onWordClick({ scope: ['*'] }))}
            >
              All
            </span>
            {scope.map((item, index) => (
              <Fragment key={`link-${item.id}`}>
                <span className={style.separator}>&gt;</span>
                {index < scope.length - 1 ? (
                  <span
                    data-testid={`stat-wc-crumb-${item.id}`}
                    className={style.link}
                    tabIndex={0}
                    onClick={() => onWordClick({ scope: [...scope.slice(0, index + 1).map(s => s.id), '*'] })}
                    onKeyDown={onEnter(() =>
                      onWordClick({ scope: [...scope.slice(0, index + 1).map(s => s.id), '*'] })
                    )}
                  >
                    {item.text}
                  </span>
                ) : (
                  <span className={style.activeWord}>{item.text}</span>
                )}
              </Fragment>
            ))}
          </div>
        ) : activeWord && !useRawData ? (
          <div className={style.links}>
            <span
              data-testid="stat-wc-crumb-all"
              className={style.link}
              tabIndex={0}
              onClick={() => onParamsChange({ activeWord: '' })}
              onKeyDown={onEnter(() => onParamsChange({ activeWord: '' }))}
            >
              All
            </span>
            <span className={style.separator}>&gt;</span>
            <span className={style.activeWord}>{activeWord}</span>
          </div>
        ) : null}

        {/* A cloud that placed nothing still owes the export an image — legacy rasterized
            whatever the view held. The live page keeps rendering nothing at all. */}
        {0 < currentState.layoutWords.length || requestId ? (
          <div id={model?.id == null ? undefined : `word_cloud_question_${model.id}`}>
            <CloudSVG
              ref={cloudRef}
              words={currentState.layoutWords}
              width={currentState.width}
              height={currentState.height}
              fontFamily={currentState.fontFamily}
              colorFn={currentState.colorFn}
              background={currentState.background}
              onWordClick={onWordClick}
              isWordClickable={isWordClickable}
              className={style.svg}
            />
          </div>
        ) : null}
      </div>
    )
  }
)

WordCloudChart.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  model: PropTypes.shape({
    id: PropTypes.string,
    useRawData: PropTypes.bool,
    changed: PropTypes.bool
  }),
  words: PropTypes.array,
  config: PropTypes.shape({
    currentPresetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activeWord: PropTypes.string,
    fontFamily: PropTypes.string,
    colorStyle: PropTypes.oneOf(['adj_levels', 'levels', 'gradient', 'circle']),
    colors: PropTypes.arrayOf(PropTypes.string),
    background: PropTypes.string,
    mask: PropTypes.string,
    customMaskPath: PropTypes.string,
    angles: PropTypes.arrayOf(PropTypes.number),
    maxWords: PropTypes.number,
    scaleFunction: PropTypes.string,
    rangeStart: PropTypes.number,
    rangeEnd: PropTypes.number,
    spell: PropTypes.oneOf([0, 1]),
    caps: PropTypes.oneOf([0, 1]),
    mergeWords: PropTypes.oneOf([0, 1]),
    twoWords: PropTypes.oneOf([0, 1]),
    junk: PropTypes.oneOf([0, 1]),
    stop1: PropTypes.oneOf([0, 1]),
    stop2: PropTypes.oneOf([0, 1]),
    stop3: PropTypes.oneOf([0, 1]),
    stop4: PropTypes.oneOf([0, 1]),
    stop5: PropTypes.oneOf([0, 1]),
    blackList: PropTypes.arrayOf(PropTypes.string),
    randIndex: PropTypes.number,
    orientation: PropTypes.oneOf(['h', 'v'])
  }).isRequired,
  scope: PropTypes.array,
  onParamsChange: PropTypes.func,
  onWordClick: PropTypes.func,
  isWordClickable: PropTypes.func
}

export default WordCloudChart
